import React from 'react';
import { Pencil } from 'lucide-react';
import TierBadge from './TierBadge';

const DEFAULT_IMAGE = 'https://api.cloudnine.biz.id/assets/img/defaultchar.png';

/**
 * Konfigurasi warna gradient background untuk setiap tier
 */
const TIER_GRADIENTS = {
    common: 'from-gray-400 to-gray-600',
    uncommon: 'from-emerald-400 to-emerald-600',
    rare: 'from-blue-400 to-blue-600',
    epic: 'from-purple-400 to-purple-600',
    legend: 'from-yellow-400 to-yellow-600',
    mythical: 'from-red-400 to-red-600',
    divine: 'from-cyan-400 via-white to-fuchsia-400',
};

/**
 * CSS untuk animasi divine tier
 */
const divineCardStyles = `
@keyframes divine-img-drift {
    0%   { transform: scale(1.18) translate(0%, 0%); }
    20%  { transform: scale(1.18) translate(2%, -3%); }
    40%  { transform: scale(1.18) translate(-1%, 2%); }
    60%  { transform: scale(1.18) translate(-3%, -1%); }
    80%  { transform: scale(1.18) translate(1%, 3%); }
    100% { transform: scale(1.18) translate(0%, 0%); }
}

@keyframes divine-overlay-shift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

@keyframes divine-border-glow {
    0%   { border-color: rgba(103, 232, 249, 0.5); }
    33%  { border-color: rgba(216, 180, 254, 0.5); }
    66%  { border-color: rgba(252, 165, 241, 0.5); }
    100% { border-color: rgba(103, 232, 249, 0.5); }
}

.divine-card-img {
    animation: divine-img-drift 12s ease-in-out infinite;
    will-change: transform;
}

.divine-overlay {
    background: linear-gradient(
        135deg,
        rgba(103, 232, 249, 0.25) 0%,
        rgba(167, 139, 250, 0.15) 25%,
        rgba(252, 165, 241, 0.25) 50%,
        rgba(167, 139, 250, 0.15) 75%,
        rgba(103, 232, 249, 0.25) 100%
    );
    background-size: 300% 300%;
    animation: divine-overlay-shift 6s ease-in-out infinite;
}

.divine-card-border {
    border-width: 1.5px;
    animation: divine-border-glow 4s ease-in-out infinite;
}

@keyframes divine-name-shift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

.divine-name {
    background: linear-gradient(
        90deg,
        #67e8e9, #c4b5fd, #fca5f1, #c4b5fd, #67e8e9
    );
    background-size: 300% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: divine-name-shift 4s ease-in-out infinite;
}
`;

/**
 * Komponen kartu karakter individual
 */
const CharacterCard = ({ character, isDarkMode, onClick, onEdit }) => {
    const {
        name,
        race,
        title,
        tier = 'common',
        image,
    } = character;

    const gradient = TIER_GRADIENTS[tier.toLowerCase()] || TIER_GRADIENTS.common;
    const displayImage = image || DEFAULT_IMAGE;
    const isDivine = tier.toLowerCase() === 'divine';

    return (
        <>
            {isDivine && <style>{divineCardStyles}</style>}
            <div
                onClick={() => onClick?.(character)}
                className={`
                    relative overflow-hidden rounded-xl flex flex-col justify-between 
                    transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer group
                    ${isDivine
                        ? 'divine-card-border shadow-[0_0_15px_rgba(167,139,250,0.15)]'
                        : (isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200')
                    }
                    h-24 md:h-auto md:aspect-[3/4]
                `}
            >
                {/* Background Image */}
                <img
                    src={displayImage}
                    alt={name}
                    className={`
                        absolute inset-0 w-full h-full object-cover
                        ${isDivine ? 'divine-card-img' : ''}
                    `}
                    loading="lazy"
                />

                {/* Dark overlay for readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Divine animated gradient overlay */}
                {isDivine && (
                    <div className="absolute inset-0 divine-overlay mix-blend-screen pointer-events-none" />
                )}

                {/* Edit Button - appears on hover */}
                {onEdit && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(character);
                        }}
                        className={`
                            absolute top-2 left-2 z-20 p-1.5 rounded-lg
                            opacity-0 group-hover:opacity-100 transition-all duration-200
                            cursor-pointer shadow-sm
                            bg-black/50 hover:bg-black/70 text-white
                        `}
                        title="Edit character"
                    >
                        <Pencil size={12} />
                    </button>
                )}

                {/* Tier Badge - top right */}
                <div className="flex justify-end items-start z-10 p-4 pb-0">
                    <TierBadge tier={tier} isDarkMode={isDarkMode} size="sm" />
                </div>

                {/* Spacer - pushes content to bottom */}
                <div className="flex-1" />

                {/* Character Details - aligned bottom */}
                <div className="z-10 p-4 pt-0 text-white">
                    <h3 className={`
                        text-[10px] md:text-sm font-semibold truncate leading-tight
                        ${isDivine ? 'divine-name' : ''}
                    `}>
                        {name}
                    </h3>
                    <p className="text-[8px] md:text-xs truncate mt-0.5 text-white/70">
                        {title}
                    </p>
                    {race && (
                        <p className="text-[7px] md:text-[10px] truncate mt-0.5 text-white/50">
                            {race}
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};

// Export gradient config
export { TIER_GRADIENTS };
export default CharacterCard;
